import React, { useEffect, useRef, useState } from 'react';
import { apiGet, apiPost } from '../../lib/api';
import { Search, Plus, Building2, X, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

export const CONSULTANT_TYPES = ['PMC', 'Cost Consultant', 'Structural Consultant', 'Architect'];

const TYPE_DESC = {
  PMC: 'Project Management Consultant',
  'Cost Consultant': 'Quantity Surveyor / Cost Estimator',
  'Structural Consultant': 'Structural Engineering Firm',
  Architect: 'Design / Architecture Firm',
};

/**
 * ConsultantSlot — single-pick consultant by type. Reads from /api/crm/companies?is_consultant=true&consultant_type=…
 * Allows inline-create of a new consultant firm.
 *
 * Props:
 *   type        one of CONSULTANT_TYPES
 *   value       { company_id, company_name } | null
 *   onChange    (next | null) => void
 */
export function ConsultantSlot({ type, value, onChange }) {
  const [firms, setFirms] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const ref = useRef(null);

  const reload = async () => {
    try {
      const res = await apiGet(`/crm/companies?is_consultant=true&consultant_type=${encodeURIComponent(type)}`);
      setFirms(res.companies || []);
    } catch { setFirms([]); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [type]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = !q ? firms : firms.filter((f) => (f.name || '').toLowerCase().includes(q));

  const pick = (firm) => {
    onChange?.({ type, company_id: firm.id, company_name: firm.name });
    setQuery('');
    setOpen(false);
  };

  const clear = () => onChange?.(null);

  const create = async () => {
    const name = query.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await apiPost('/crm/companies', { name, is_consultant: true, consultant_type: type });
      const newFirm = res.company || res;
      setFirms((prev) => [...prev, newFirm]);
      pick(newFirm);
      toast.success(`${type} firm "${name}" added`);
    } catch (e) {
      toast.error(e?.detail || 'Failed to create consultant firm');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="border border-slate-200 rounded-sm p-3 bg-slate-50/40" data-testid={`consultant-slot-${type.replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Briefcase size={12} className="text-[#1F7A6E]" />
        <div className="text-[10px] uppercase tracking-widest font-display font-bold text-[#1F7A6E]">{type}</div>
      </div>
      <div className="text-[10px] text-slate-500 mb-2">{TYPE_DESC[type]}</div>
      {value?.company_id ? (
        <div className="flex items-center justify-between bg-white border border-slate-200 rounded-sm px-2.5 py-1.5">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 size={13} className="text-[#1F7A6E] shrink-0" />
            <div className="text-xs font-semibold truncate">{value.company_name}</div>
          </div>
          <button type="button" onClick={clear} className="text-slate-400 hover:text-red-600" title="Clear">
            <X size={13} />
          </button>
        </div>
      ) : (
        <div className="relative" ref={ref}>
          <div className="bg-white border border-slate-300 rounded-sm px-2 py-1.5 flex items-center gap-1.5 focus-within:border-[#1F7A6E]">
            <Search size={12} className="text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder={`Pick or add ${type} firm…`}
              className="flex-1 text-xs border-0 focus:outline-none bg-transparent"
              data-testid={`consultant-slot-${type.replace(/\s+/g, '-')}-input`}
            />
          </div>
          {open && (
            <div className="absolute left-0 right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg z-30 max-h-52 overflow-y-auto">
              {filtered.length === 0 && (
                <div className="px-3 py-2 text-xs text-slate-500 italic">
                  {q ? `No firms match "${q}". ` : `No ${type} firms yet. `}
                </div>
              )}
              {filtered.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => pick(f)}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex items-center gap-2 border-b border-slate-100 last:border-b-0"
                  data-testid={`consultant-pick-${f.id}`}
                >
                  <Building2 size={11} className="text-slate-400" />
                  <span className="font-semibold truncate">{f.name}</span>
                  {f.city && <span className="text-[10px] text-slate-500">· {f.city}</span>}
                </button>
              ))}
              {q && !firms.some((f) => (f.name || '').toLowerCase() === q) && (
                <button
                  type="button"
                  disabled={creating}
                  onClick={create}
                  className="w-full text-left px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 flex items-center gap-2 border-t border-slate-200"
                  data-testid={`consultant-slot-${type.replace(/\s+/g, '-')}-create`}
                >
                  <Plus size={11} /> {creating ? 'Creating…' : `Add new ${type} firm "${query}"`}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * ConsultantPanel — bundles all 4 slots vertically. Internal state shape:
 *   value = [{type, company_id, company_name}, ...]   (up to 4 entries, one per type)
 */
export default function ConsultantPanel({ value = [], onChange }) {
  const byType = Object.fromEntries((value || []).map((c) => [c.type, c]));
  const set = (type, next) => {
    const others = (value || []).filter((c) => c.type !== type);
    onChange?.(next ? [...others, next] : others);
  };
  return (
    <div className="space-y-2" data-testid="consultants-panel">
      <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Consultants / Architects</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {CONSULTANT_TYPES.map((t) => (
          <ConsultantSlot key={t} type={t} value={byType[t] || null} onChange={(v) => set(t, v)} />
        ))}
      </div>
    </div>
  );
}
